import React, { FC, useState } from 'react';
import TwDialog from '../../../TwDialog/TwDialog';
import { TwButton, TwButtonType, TwLoadingButton } from '../../../TwButtons/TwButtons';
import TwField, { StyledTextfieldLabel, TwInputTypes } from '../../../TwFields/TwFields';
import {
  usePatchTripByTokenMutation,
  usePatchTripMutation
} from '../../../../redux/api/trips/tripApi';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import { useLocation } from 'react-router-dom';
import RTKUtils from '../../../../utils/rtkUtils';
import { Trip } from '../../../../redux/api/trips/trips.model';
import { setTripDetailReload } from '../../../../redux/features/navigation/navigationSlice';
import { useAppDispatch } from '../../../../redux/hooks';
import { useAllowCharacters, Characters } from '../../../../hooks/useAllowCharacters';

export enum MileageType {
  Loaded = 'Loaded',
  Empty = 'Empty'
}

interface AddEditMilesProps {
  tripId: string;
  mileageType: MileageType;
  miles?: number;
  isEdit?: boolean;
  open: boolean;
  onClose: () => void;
  reloadData?: () => void;
  token?: string;
}

const AddEditMiles: FC<AddEditMilesProps> = ({
  tripId,
  mileageType,
  open,
  onClose,
  isEdit,
  miles,
  reloadData,
  token
}): JSX.Element => {
  const [currentMiles, setCurrentMiles] = useState<number | undefined>(miles);
  const route = useLocation();
  const dispatch = useAppDispatch();

  const [patchTrip] = usePatchTripMutation();
  const [patchTripByToken] = usePatchTripByTokenMutation();

  const { addNotification } = useNotifications();
  const tagsToInvalidate = RTKUtils.invalidateTagsForOrdersAndTrips(route.pathname);
  const mileageKey = `${
    mileageType === MileageType.Empty ? 'empty_miles' : 'estimated_mileage'
  }` as keyof Trip;
  const mileageOverrideKey = `${
    mileageType === MileageType.Empty ? 'is_empty_miles_auto' : 'is_loaded_miles_auto'
  }` as keyof Trip;

  const handleKeyDownCharacters = useAllowCharacters(Characters.float);

  const handleMilesChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    const value = e.target.value;
    if (value === '') {
      setCurrentMiles(undefined);
    } else if (!isNaN(Number(value))) {
      setCurrentMiles(Number(value));
    }
  };

  const handleSaveMileage = (): void => {
    const patchData = { [mileageKey]: currentMiles || null, [mileageOverrideKey]: false };
    (token
      ? patchTripByToken({
          tripId: tripId,
          token,
          body: patchData
        })
      : patchTrip({
          id: tripId || '',
          body: patchData,
          tagsToInvalidate
        })
    )
      .unwrap()
      .then(() => {
        addNotification({
          message: 'Mileage has been successfully updated.',
          variant: 'success'
        });
        dispatch(setTripDetailReload(true));
        reloadData && reloadData();
      })
      .catch((error: any) => {
        let message = 'Error updating mileage. Please try again.';
        if (error.data.error.includes('confirm/accept')) {
          message = 'Stop cannot be updated. Please confirm/accept the trip to update it.';
        }

        addNotification({
          message,
          variant: 'error'
        });
      });

    onClose();
  };

  return (
    <>
      <TwDialog
        open={open}
        title={`${isEdit ? 'Edit' : 'Add'} ${
          mileageType === MileageType.Empty ? 'empty' : 'loaded'
        } miles`}
        actionsAlignment="flex-end"
        onClickClose={onClose}
        actions={[
          <TwButton
            key="1"
            type={TwButtonType.secondary}
            label="Cancel"
            action={(): void => {
              onClose();
            }}
          />,
          <TwLoadingButton
            key="2"
            loading={false}
            type={TwButtonType.primary}
            label="Save"
            action={handleSaveMileage}
          />
        ]}
        sx={{
          zIndex: '1500 !important'
        }}
      >
        <>
          <TwField
            name="mileage"
            className="w-144"
            label={`${mileageType === MileageType.Empty ? 'Empty' : 'Loaded'} mileage`}
            inputType={TwInputTypes.number}
            value={currentMiles}
            autoComplete="off"
            onInputChange={handleMilesChange}
            onKeyDown={handleKeyDownCharacters}
            endAdorment={<StyledTextfieldLabel>mi</StyledTextfieldLabel>}
          />
        </>
      </TwDialog>
    </>
  );
};

export default AddEditMiles;
